import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { ProjectId } from 'api/models';
import { Project } from 'api/project';
import {
  AccountVisibility,
  VisibilityAPI,
  VisibilityId,
  VisibilityMeta,
} from 'api/visibility';
import { getProjectDetailQueryKey } from 'queries/common';
import { logger } from 'services';
import { update } from 'stores/visibility';

type CreateAccountVisibilitySettings = (newVisibility: VisibilityMeta) => void;

export const useCreateAccountVisibility =
  (): CreateAccountVisibilitySettings => {
    const dispatch = useDispatch();

    return useCallback(
      async (newVisibility: VisibilityMeta) => {
        try {
          const result = await VisibilityAPI.createAccountVisibilitySettings(
            newVisibility,
          );

          dispatch(update(result));
        } catch (error) {
          logger.error('create account visibility failed', {
            error: error as Error,
          });
        }
      },
      [dispatch],
    );
  };

type CreateProjectVisibilitySettings = (newVisibility: VisibilityMeta) => void;

export const useCreateProjectVisibility = (
  projectId,
): CreateProjectVisibilitySettings => {
  const queryClient = useQueryClient();

  return useCallback(
    async (newVisibility: VisibilityMeta) => {
      try {
        const result = await VisibilityAPI.createProjectVisibilitySettings(
          projectId,
          newVisibility,
        );

        await queryClient.invalidateQueries(
          getProjectDetailQueryKey(projectId),
        );

        return result;
      } catch (error) {
        logger.error('create project visibility failed', {
          error: error as Error,
        });

        return error;
      }
    },
    [projectId, queryClient],
  );
};

type UpdateVisibilitySettings = (
  visibilityMeta: VisibilityMeta,
  visibilityId: VisibilityId,
) => void;

export const useUpdateAccountVisibility = (): UpdateVisibilitySettings => {
  const dispatch = useDispatch();

  return useCallback(
    async (visibilityMeta: VisibilityMeta, visibilityId: VisibilityId) => {
      try {
        const result = await VisibilityAPI.updateAccountVisibilitySettings(
          visibilityId,
          visibilityMeta,
        );

        dispatch(update(result as AccountVisibility));
      } catch (error) {
        logger.error('update account visibility failed', {
          error: error as Error,
        });
      }
    },
    [dispatch],
  );
};

export const useUpdateProjectVisibility = (
  projectId,
): UpdateVisibilitySettings => {
  const queryClient = useQueryClient();

  return useCallback(
    async (visibilityMeta: VisibilityMeta, visibilityId: VisibilityId) => {
      try {
        const result = await VisibilityAPI.updateProjectVisibilitySettings(
          visibilityId,
          visibilityMeta,
        );

        await queryClient.invalidateQueries(
          getProjectDetailQueryKey(projectId),
        );

        return result;
      } catch (error) {
        logger.error('update project visibility failed', {
          error: error as Error,
        });

        return error;
      }
    },
    [projectId, queryClient],
  );
};

export const useUpdateProjectHourVisibility = (
  projectId,
): UpdateVisibilitySettings => {
  const queryClient = useQueryClient();

  return useCallback(
    async (visibilityMeta: VisibilityMeta, visibilityId: VisibilityId) => {
      const project = queryClient.getQueryData<Project>(ProjectId(projectId));

      try {
        const result = await VisibilityAPI.updateProjectVisibilitySettings(
          visibilityId,
          visibilityMeta,
        );

        if (project && project.visibilitySettings) {
          const updatedVisibilitySettings = {
            ...project.visibilitySettings,
            visibilityMeta: {
              ...project.visibilitySettings.visibilityMeta,
              projectHoursToggle: visibilityMeta.projectHoursToggle,
            },
          };

          const queryKey = getProjectDetailQueryKey(ProjectId(projectId));

          queryClient.setQueryData<Project>(queryKey, () => ({
            ...project,
            visibilitySettings: updatedVisibilitySettings,
          }));
        }

        return result;
      } catch (error) {
        logger.error('update project hour visibility failed', {
          error: error as Error,
        });

        return error;
      }
    },
    [projectId, queryClient],
  );
};
